// Lightbox for article images (.p-prose) and the hero image.
// The clicked image morphs into the enlarged one (same-document View Transition).
// Browsers without the API (or with reduced motion at the time of the action) get a plain fade.
// Images are keyboard operable (Enter / Space); while open, the page behind is inert and focus
// stays in the dialog, then returns to the image that opened it.
const IMAGE_SELECTOR = '.p-prose img, .p-hero img';
// Images inside a link card (the whole card is a link) or any other link must keep working as links.
const EXCLUDE_SELECTOR = '.p-linkcard, a[href]';
const TARGET_CLASS = 'js-lightbox-target';
const BODY_LOCK_CLASS = 'is-lightbox-open';
const NAME = 'd-lightbox-image';

const parseSrcset = (srcset) => {
  if (!srcset) return [];
  return srcset
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [url, descriptor] = entry.split(/\s+/);
      const width = descriptor && descriptor.endsWith('w') ? parseInt(descriptor, 10) : 0;
      return { url, width };
    })
    .filter(({ url }) => Boolean(url));
};

// The largest srcset candidate of the same list the browser picked from.
// <picture> sources carry different formats (avif / webp / jpeg): never mix them, so the list that
// contains img.currentSrc is used, and its widest candidate is returned. No match: keep currentSrc.
const absolute = (url) => { try { return new URL(url, document.baseURI).href; } catch (e) { return url; } };
const pickLargestSrc = (img) => {
  const current = img.currentSrc || img.src;
  if (!img.currentSrc) return current;
  const picture = img.closest('picture');
  const sets = [
    ...(picture ? [...picture.querySelectorAll('source[srcset]')].map((s) => s.srcset) : []),
    img.srcset,
  ];
  for (const set of sets) {
    const candidates = parseSrcset(set);
    if (!candidates.some((c) => absolute(c.url) === img.currentSrc)) continue;
    const best = candidates.reduce((m, c) => (c.width > m.width ? c : m), { url: '', width: 0 });
    return best.width > 0 ? absolute(best.url) : current;
  }
  return current;
};

const init = () => {
  const images = [...document.querySelectorAll(IMAGE_SELECTOR)].filter((img) => !img.closest(EXCLUDE_SELECTOR));
  if (!images.length) return;
  images.forEach((img) => {
    img.classList.add(TARGET_CLASS);
    img.tabIndex = 0;
    img.setAttribute('role', 'button');
    img.setAttribute('aria-label', img.alt ? `Enlarge image: ${img.alt}` : 'Enlarge image');
  });
  const imageSet = new Set(images);

  // read at the time of each action
  const canMorph = () => 'startViewTransition' in document && !matchMedia('(prefers-reduced-motion: reduce)').matches;
  let state = null; // { img, box, opener, inerted } while open
  let busy = false; // a transition is running: ignore open / close requests

  const build = (img) => {
    const el = document.createElement('div');
    el.className = 'p-lightbox is-open';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', img.alt || 'Image');
    el.tabIndex = -1;
    const figure = document.createElement('figure');
    const big = document.createElement('img');
    big.alt = img.alt || '';
    big.src = img.currentSrc || img.src; // already loaded: appears at once, upgraded below
    figure.appendChild(big);
    if (img.alt) {
      const cap = document.createElement('figcaption');
      cap.textContent = img.alt;
      figure.appendChild(cap);
    }
    el.appendChild(figure);
    const largest = pickLargestSrc(img);
    if (largest && largest !== big.src) {
      const loader = new Image();
      loader.onload = () => { if (state && state.box === el) big.src = largest; };
      loader.onerror = () => {}; // keep the image already shown
      loader.src = largest;
    }
    el.addEventListener('click', () => close());
    return el;
  };

  const lock = () => {
    const gap = innerWidth - document.documentElement.clientWidth;
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
    document.body.classList.add(BODY_LOCK_CLASS);
  };
  const unlock = () => {
    document.body.classList.remove(BODY_LOCK_CLASS);
    document.body.style.paddingRight = '';
  };
  // Make everything else on the page inert (header, main, footer ...). Returns what was changed.
  const inertOthers = (box) => {
    const changed = [...document.body.children].filter(
      (el) => el !== box && el.tagName !== 'SCRIPT' && !el.inert
    );
    changed.forEach((el) => { el.inert = true; });
    return changed;
  };

  function open(img) {
    if (state || busy) return;
    const st = { img, box: null, opener: img, inerted: [] };
    state = st;
    const mount = () => {
      st.box = build(img);
      return st.box;
    };
    const show = (box) => {
      document.body.append(box);
      st.inerted = inertOthers(box);
      lock();
      box.focus({ preventScroll: true });
    };
    if (canMorph()) {
      busy = true;
      img.style.viewTransitionName = NAME;
      const t = document.startViewTransition(() => {
        img.style.viewTransitionName = '';
        const box = mount();
        box.querySelector('img').style.viewTransitionName = NAME;
        show(box);
      });
      t.finished.catch(() => {}).finally(() => { img.style.viewTransitionName = ''; busy = false; });
    } else {
      const box = mount();
      box.classList.remove('is-open');
      show(box);
      requestAnimationFrame(() => box.classList.add('is-open'));
    }
  }

  function close() {
    if (!state || busy || !state.box) return;
    const { img, box, opener, inerted } = state; // captured per operation
    state = null;
    const restore = () => {
      inerted.forEach((el) => { el.inert = false; });
      unlock();
      opener.focus({ preventScroll: true });
    };
    if (canMorph()) {
      busy = true;
      const t = document.startViewTransition(() => {
        box.remove();
        img.style.viewTransitionName = NAME;
        restore();
      });
      t.finished.catch(() => {}).finally(() => { img.style.viewTransitionName = ''; busy = false; });
    } else {
      busy = true;
      box.classList.remove('is-open');
      const done = () => { box.remove(); restore(); busy = false; };
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else setTimeout(done, 250);
    }
  }

  document.addEventListener('click', (event) => {
    if (state || !(event.target instanceof Element)) return;
    const img = event.target.closest('img');
    if (!img || !imageSet.has(img)) return;
    event.preventDefault();
    open(img);
  });
  document.addEventListener('keydown', (event) => {
    if (state) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab' && state.box) {
        event.preventDefault(); // the dialog is the only focusable thing: keep focus on it
        state.box.focus({ preventScroll: true });
      }
      return;
    }
    if ((event.key === 'Enter' || event.key === ' ') && event.target instanceof Element && imageSet.has(event.target)) {
      event.preventDefault();
      open(event.target);
    }
  });
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
